import { useCallback, useRef } from 'react';
import useEffectOnce from '@/hooks/useEffectOnce';

import _ from 'lodash';

type HandlePushCallback = (args: { data: any; key: string }) => void;

type HandleRemoveCallback = (args: { key: string; value: string }) => void;

type HasCallback = (args: { key: string; value?: string }) => boolean;

export const useDataLayer = (
  effect?: (args: {
    /**
     * 데이터 레이어 추가
     */
    handlePush: HandlePushCallback;
    /**
     * 데이터레이어 삭제
     */
    handleRemove: HandleRemoveCallback;
    /**
     * key or key:value 기준으로 존재하는지
     */
    has: HasCallback;
    /**
     * 데이터 레이어 추가후 바로 삭제
     */
    handlePushAndRemove: HandlePushCallback;
  }) => void | (() => void),
) => {
  const remainEffect = useRef<boolean>(true);
  const has = useCallback<HasCallback>(({ key, value }) => {
    if (_.isEmpty(value))
      return window?.dataLayer?.some((item) => _.has(item, key));
    return window?.dataLayer?.some((item) => item?.[key] === value);
  }, []);

  const handlePush = useCallback<HandlePushCallback>(({ data, key }) => {
    if (_.isEmpty(data[key])) return;
    // if (has({ key, value: data[key] })) return;

    window?.dataLayer?.push({
      ...data,
    });
  }, []);

  const handleRemove = useCallback<HandleRemoveCallback>(
    ({ key, value }) => {
      if (!has({ key, value })) return;
      const index = window?.dataLayer?.findIndex(
        (item) => item?.[key] === value,
      );
      window.dataLayer.splice(index, 1);
    },
    [has],
  );

  const handlePushAndRemove = useCallback<HandlePushCallback>(
    ({ data, key }) => {
      handlePush({ data, key });
      // handleRemove({ key, value: data[key] });
    },
    [handlePush],
  );

  const isContainerLoad = useCallback(() => {
    return (window as any)?.google_tag_manager?.dataLayer?.gtmLoad;
  }, []);

  useEffectOnce(() => {
    const remainEffectHandler = () => {
      if (remainEffect.current) {
        remainEffect.current = false;
        return effect?.({
          handlePush,
          handleRemove,
          has,
          handlePushAndRemove,
        });
      }
    };
    window.addEventListener('load', remainEffectHandler);
    let destroyFunc: Function | void;
    if (isContainerLoad()) {
      destroyFunc = remainEffectHandler();
    }
    return () => {
      destroyFunc?.();
      window.removeEventListener('load', remainEffectHandler);
    };
  });

  return {
    handlePush,
    handleRemove,
    has,
    handlePushAndRemove,
  };
};
